import { createSlice } from "@reduxjs/toolkit";
import { rgsPlay } from "./spinSlice";
import { initializeGame, refreshGameData, syncFreeRoundsWin } from "./gameSettingsSlice";
// Cross-slice listen (same pattern as rgsPlay above) — replay's own init is a
// separate fetch from initializeGame, so FR state has to be seeded from it too,
// and the live campaign has to be preserved across the session.
import {
  fetchReplayInit,
  startReplaySession,
  endReplaySession,
} from "./replaySlice";

import { FEATURES_CONFIG } from "../../featuresConfig";
import StorageManager from "../../utils/StorageManager";

// The FR fields a replay session overwrites — snapshotted on entry, restored
// on exit. Kept as one list so the two can't drift apart.
const REPLAY_SNAPSHOT_FIELDS = [
  "status",
  "betValue",
  "performed",
  "count",
  "win",
  "displayedWin",
  "syncedWin",
  "winCounted",
  "completionPending",
  "freeRoundsEnded",
  "reloadPending",
  "reloadWinWithheld",
];

/**
 * Free Rounds Slice
 * Manages casino-granted free rounds (promotional spins)
 *
 * Data source: PopiPlay API `free_rounds` top-level object + `balance.free_rounds`
 *
 * State fields:
 * - status: "active" | null  (never set to "played" — completionPending gates the outro)
 * - betValue: Fixed bet amount for free rounds (in subunits)
 * - performed: Number of free rounds completed so far
 * - count: Total number of free rounds granted
 * - win: Cumulative win from balance.free_rounds
 * - displayedWin: Optimistic total win — incremented on each spin, synced from server
 * - winCounted: True once the CURRENT round's win is already inside displayedWin
 * - completionPending: True after last round API response, stays true through popup sequence
 * - freeRoundsEnded: True after info popup opens (signals WinDisplay to freeze)
 * - reloadPending: True on reload during active FR (show reload popup)
 */

const initialState = {
  status: null,
  betValue: null,
  performed: 0,
  count: 0,
  win: 0,
  displayedWin: 0,
  syncedWin: null,
  // ── Is the CURRENT round's win already inside displayedWin? ──
  //
  // The increment below is optimistic and fires from a presentation, and a
  // presentation can run twice for one round: leaving a replay re-presents the
  // restored round on purpose. Without a record of what has been counted, the
  // second pass adds the same money again — the free-rounds total grows by the
  // replayed win on every replay, for ever.
  //
  // A FLAG and not a round id, because live rounds do not have one:
  // adaptSpinRoundToState builds a fresh object and drops `id`, and only the
  // replay path re-stamps it. Keyed on an id, this guard would silently never
  // engage in live play — which is the bug it exists to stop.
  //
  // Snapshotted with the rest, and that is what makes it exact: the replay's
  // own increments raise it inside the session, endReplaySession restores the
  // LIVE round's value, and so a round that was counted is recognised while a
  // round that was NOT — the player opened History mid-counter — still gets
  // its money on the way back.
  winCounted: false,
  completionPending: false,
  freeRoundsEnded: false,
  reloadPending: false,
  // True when a reload took the restored round's win OFF the total so the
  // replay can put it back — see initializeGame.fulfilled below. The win
  // display's manual increment skips reloads by default (the server's total
  // already holds the win); this is what tells it that this time it does not.
  reloadWinWithheld: false,
  // The live campaign's FR state, parked while a replay session borrows this
  // slice for the replayed round's indicators. null when not replaying.
  preReplaySnapshot: null,
};

const freeRoundsSlice = createSlice({
  name: "freeRounds",
  initialState,
  reducers: {
    /**
     * Set free rounds data directly
     * @param {object} action.payload - { status, bet_value, performed, count, win }
     */
    setFreeRounds(state, action) {
      const { status, bet_value, performed, count, win } = action.payload;
      state.status = status;
      state.betValue = bet_value;
      state.performed = performed;
      state.count = count;
      state.win = win !== undefined ? win : 0;
      state.freeRoundsEnded = false;
    },

    /**
     * Clear free rounds state after completion popups.
     * Preserves displayedWin and freeRoundsEnded so WinDisplay
     * can show "Total Win: X" until the next spin clears them.
     * Same pattern as pot collection (potCollectionOutroCounterFinished + currentRound.win).
     */
    clearFreeRounds(state) {
      const preservedWin = state.displayedWin;
      // preReplaySnapshot must survive: this runs during a replayed campaign's
      // completion, and initialState would null it — destroying the live
      // campaign parked on session entry, so the exit restore would silently
      // do nothing and leave the replayed round's Total Win on screen.
      const preservedSnapshot = state.preReplaySnapshot;
      Object.assign(state, initialState);
      state.displayedWin = preservedWin;
      state.freeRoundsEnded = true;
      state.preReplaySnapshot = preservedSnapshot;
    },

    setCompletionPending(state) {
      state.completionPending = true;
    },

    clearCompletionPending(state) {
      state.completionPending = false;
    },

    setFreeRoundsEnded(state) {
      state.freeRoundsEnded = true;
    },

    clearFreeRoundsEnded(state) {
      state.freeRoundsEnded = false;
    },

    setReloadPending(state) {
      state.reloadPending = true;
    },

    clearReloadPending(state) {
      state.reloadPending = false;
      state.reloadWinWithheld = false;
    },

    /**
     * Optimistically increment displayedWin by the current spin's win amount.
     * Called by WinDisplay when a win is shown during free rounds.
     * Server syncs on next spin response via balance.free_rounds.
     */
    incrementDisplayedWin(state, action) {
      const amount = action.payload;
      if (!(amount > 0)) return;
      state.displayedWin += amount;
      state.winCounted = true;
      // A withheld reload win is paid back exactly once.
      state.reloadWinWithheld = false;
    },

    /**
     * Clear preserved FR win data on next spin.
     * Mirrors clearPotCollectionWin — called by WinDisplay when isSpinning.
     */
    clearFreeRoundsWin(state) {
      state.reloadWinWithheld = false;
      state.displayedWin = 0;
      state.syncedWin = null;
      state.freeRoundsEnded = false;
      // The record describes displayedWin; it cannot outlive it.
      state.winCounted = false;
    },

    /**
     * Apply the pre-fetched server total win to displayedWin.
     * Called when the animated win counter finishes — the sync request
     * was fired early (when animation started) to hide network latency.
     */
    applySyncedWin(state) {
      if (state.syncedWin !== null) {
        state.win = state.syncedWin;
        state.displayedWin = state.syncedWin;
        state.syncedWin = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ── Spin pending: clear completed total win ──
      .addCase(rgsPlay.pending, (state) => {
        if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED) return;
        // Nothing to do — displayedWin and freeRoundsEnded are cleared
        // by WinDisplay via clearFreeRoundsWin() on next spin.
      })

      // ── Spin fulfilled: update from API response ──
      .addCase(rgsPlay.fulfilled, (state, action) => {
        if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED) return;

        // A new spin response: displayedWin is about to be re-assigned from the
        // server below, so nothing is owed and nothing has been counted for the
        // round that is arriving. Cleared here, once, ahead of every branch.
        state.winCounted = false;

        const { freeRounds, freeRoundsTotalWin } = action.payload;

        if (freeRounds?.status === "active") {
          // Don't reactivate if we're mid-completion
          if (state.completionPending) return;

          state.status = "active";
          state.betValue = freeRounds.bet_value;
          state.performed = freeRounds.performed;
          state.count = freeRounds.count;
        } else if (freeRounds?.status === "played") {
          // ── Last round: API says "played" but we keep status="active" ──
          // UI must stay visible (counter, total win, win) while completion
          // popups are shown. completionPending is the sole signal.
          if (state.completionPending) return; // Already detected
          state.performed = freeRounds.performed;
          state.count = freeRounds.count;
          state.completionPending = true;
          // status stays "active" — selectFreeRoundsActive remains true
        } else if (freeRounds?.status === "canceled") {
          // ── FR canceled externally (operator revoked) ──
          // Deactivate FR immediately. The bonus continues normally,
          // but the FR counter/banner should disappear.
          state.status = null;
          state.betValue = null;
          state.performed = 0;
          state.count = 0;
          state.win = 0;
          state.completionPending = false;
        } else if (freeRounds === null && state.status !== null && !state.completionPending) {
          // API no longer sends free_rounds → back to base game
          state.status = null;
          state.betValue = null;
          state.performed = 0;
          state.count = 0;
          state.win = 0;
        }

        // Sync win from balance.free_rounds (server source of truth)
        if (freeRoundsTotalWin !== undefined && freeRoundsTotalWin !== null) {
          state.win = freeRoundsTotalWin;
          state.displayedWin = freeRoundsTotalWin;
        }
      })

      // ── Replay session entry: park the live campaign ──
      // A replayed free round writes the RECORDED round's counter/total into
      // this slice. Without stashing what was here first, exiting replay left
      // the live game believing a campaign was running: the FR counter stayed
      // on screen and the next real spin popped "206 — Your Free Spins ended
      // or expired", for a campaign the player never actually had.
      //
      // Snapshot rather than clear-on-exit, because the player may genuinely
      // be mid-campaign and replaying an old round — their real counter and
      // total have to come back exactly as they were.
      //
      // Only the FIRST start of a session snapshots: "Replay Again" dispatches
      // startReplaySession too, and by then this slice already holds replay
      // data, which would overwrite the live values we're protecting.
      .addCase(startReplaySession, (state) => {
        if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED) return;
        if (state.preReplaySnapshot) return;
        const snapshot = {};
        for (const field of REPLAY_SNAPSHOT_FIELDS) snapshot[field] = state[field];
        state.preReplaySnapshot = snapshot;
      })

      // ── Replay session exit: hand the live campaign back ──
      // Restores whatever was parked above — an active campaign returns intact,
      // and "no campaign" correctly clears the replayed round's indicators.
      .addCase(endReplaySession, (state) => {
        if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED) return;
        const snapshot = state.preReplaySnapshot;
        if (!snapshot) return;
        for (const field of REPLAY_SNAPSHOT_FIELDS) state[field] = snapshot[field];
        state.preReplaySnapshot = null;
      })

      // ── Replay session start (docs/replay-history-spec.md §0) ──
      // Seeds the FR indicators from the replayed round's own init response so
      // they're on screen as soon as the Enter modal closes — the player should
      // see "Free Rounds: 5/15" and the running total immediately, not only
      // after pressing Spin (which is when the first step response arrives).
      //
      // Deliberately simpler than initializeGame below: no StorageManager
      // withholding (that's live reload-recovery for a win about to replay),
      // and completionPending is left false — the campaign's completion is
      // driven later by the replayed round's own "played" status, not seeded
      // here at entry.
      .addCase(fetchReplayInit.fulfilled, (state, action) => {
        if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED) return;

        const { freeRounds, freeRoundsTotalWin } = action.payload;

        if (freeRounds?.status === "active" || freeRounds?.status === "played") {
          state.status = "active";
          state.betValue = freeRounds.bet_value;
          state.performed = freeRounds.performed;
          state.count = freeRounds.count;
          state.win = freeRoundsTotalWin ?? 0;
          state.displayedWin = freeRoundsTotalWin ?? 0;
          state.syncedWin = null;
          state.completionPending = false;
          state.freeRoundsEnded = false;
          state.reloadPending = false;
        } else {
          // Replaying a round that wasn't part of a campaign — clear any
          // indicators a previously replayed free round left behind.
          state.status = null;
          state.betValue = null;
          state.performed = 0;
          state.count = 0;
          state.win = 0;
          state.displayedWin = 0;
          state.syncedWin = null;
          state.completionPending = false;
          state.freeRoundsEnded = false;
          state.reloadPending = false;
        }
      })
      // ── Game init: detect free rounds from API ──
      .addCase(initializeGame.fulfilled, (state, action) => {
        if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED) return;

        const { result } = action.payload;
        const { freeRounds, freeRoundsTotalWin } = result;

        if (freeRounds?.status === "active") {
          state.status = "active";
          state.betValue = freeRounds.bet_value;
          state.performed = freeRounds.performed;
          state.count = freeRounds.count;
          state.win = freeRoundsTotalWin ?? 0;
          state.displayedWin = freeRoundsTotalWin ?? 0;
          // ── Withhold the win the reload is about to REPLAY — any win ──
          //
          // The server's total already contains the restored round's win, and
          // the reload replays that round in full. Left level, the Total Win
          // sat at the final figure while the win it belongs to was still to
          // be shown. It used to be withheld for a BIG win only; an ordinary
          // one stayed in and the replay never moved the row. Vladimir,
          // 2026-09-24: *"we remove total win, we show entire sequence, again
          // show win, add it to total win again."*
          //
          // Read off the round the RGS actually restored — what will be
          // presented — not the storage copy. Paid back by the win display:
          // a big win through its synced-total path, an ordinary one through
          // the manual increment, which `reloadWinWithheld` lets through.
          //
          // NOT while the server still owes a feature on that round. A win
          // that triggered free spins is carried into the bonus, and the server
          // books it into `balance.free_rounds` only when the bonus closes —
          // so the total does not contain it yet, and taking it off would
          // leave the row short.
          const replayed = result.round;
          const featureOwed = (result.availableActions || []).some(
            (a) => a !== "init" && a !== "spin",
          );
          const replayWin =
            !featureOwed &&
            !replayed?.boardSettled &&
            replayed?.triggerWin &&
            replayed.win > 0
              ? replayed.win
              : 0;
          if (replayWin > 0) {
            state.displayedWin = Math.max(0, state.displayedWin - replayWin);
            state.reloadWinWithheld = true;
          }
          state.reloadPending = true;
        } else if (freeRounds?.status === "played") {
          // ── Completion reload: FR finished but user reloaded before next spin ──
          // RGS.initGame() synthesises this from localStorage. Keep UI alive.
          state.status = "active";
          state.betValue = freeRounds.bet_value;
          state.performed = freeRounds.performed;
          state.count = freeRounds.count;
          state.win = freeRoundsTotalWin ?? 0;
          state.displayedWin = freeRoundsTotalWin ?? 0;
          // Withhold last round's win — counter must replay before Total Win reveals it.
          // If this is the first/only win, displayedWin drops to 0 → Total Win hidden
          // until WinDisplay's natural increment fires after counter finishes.
          // Safe from compounding: (totalWin - roundWin) + roundWin = totalWin.
          //
          // ONLY valid when lastRoundApplied is true — meaning `win` above already
          // had the last round's win folded in by a completed live reveal. If the
          // user reloaded before that reveal finished, `win` is still the pre-round
          // baseline (nothing to withhold) — withholding here would incorrectly
          // subtract a win that was never added, silently dropping it from the total.
          //
          // ── A BONUS on the last round: its replay adds the ROOM's total ──
          //
          // When the last free round won free spins, the reload restores the
          // room at its completion and the replay's exit adds the room total
          // to this figure (TransitionDialog, the last-round branch). The base
          // round is a settled board that replays nothing, and its win is
          // inside the room total already. So what the replay pays back is the
          // ROOM's total, and that is what comes off — or every reload after
          // the exit added the bonus again: 1864 -> 1869 -> 1874. Vladimir,
          // 2026-09-24: *"we must always have same value and real value."*
          const restoredRoom = result.freeSpinsState?.phase === "completion";
          const replayWin = restoredRoom
            ? (result.freeSpinsState?.data?.totalWin ?? 0)
            : (StorageManager.loadBaseGameRound()?.round?.win ?? 0);
          if (freeRounds.lastRoundApplied !== false && replayWin > 0) {
            state.displayedWin = Math.max(0, state.displayedWin - replayWin);
          }
          state.completionPending = true;
        }
      })

      // ── Refresh game data (error 205 flow) ──
      .addCase(refreshGameData.fulfilled, (state, action) => {
        if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED) return;

        const { result } = action.payload;
        const { freeRounds, freeRoundsTotalWin } = result;

        // Activate free rounds from refresh (error 205 → dismiss → re-init)
        // Only activate if not already active or mid-completion
        if (
          freeRounds?.status === "active" &&
          state.status !== "active" &&
          !state.completionPending
        ) {
          state.status = "active";
          state.betValue = freeRounds.bet_value;
          state.performed = freeRounds.performed;
          state.count = freeRounds.count;
          state.win = freeRoundsTotalWin ?? 0;
          state.displayedWin = freeRoundsTotalWin ?? 0;
          state.reloadPending = true;
        }

        // Deactivate free rounds when server says they're gone but client
        // still thinks they're active (external cancellation/expiration,
        // e.g. FR finished in another tab or promo expired by casino).
        // Full reset — no completion popups, no preserved win.
        if (
          (!freeRounds || freeRounds.status !== "active") &&
          state.status === "active" &&
          !state.completionPending
        ) {
          // Same caveat as clearFreeRounds: never let a wholesale reset drop a
          // parked live campaign, or the replay exit restore becomes a no-op.
          const preservedSnapshot = state.preReplaySnapshot;
          Object.assign(state, initialState);
          state.preReplaySnapshot = preservedSnapshot;
        }
      })

      // ── Sync free rounds win from server (pre-fetch during animation) ──
      // Stores in staging field — applied to displayedWin by applySyncedWin
      // when the counter finishes.
      .addCase(syncFreeRoundsWin.fulfilled, (state, action) => {
        if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED) return;
        const { freeRoundsTotalWin } = action.payload;
        if (freeRoundsTotalWin !== undefined && freeRoundsTotalWin !== null) {
          state.win = freeRoundsTotalWin;
          state.syncedWin = freeRoundsTotalWin;
        }
      });
  },
});

// ==============================
// 🔹 Selectors
// ==============================

// Returns true when free rounds UI should be visible.
// Includes completion phase — UI stays alive until clearFreeRounds() is dispatched.
export const selectFreeRoundsActive = (state) =>
  state.freeRounds.status === "active" || state.freeRounds.completionPending;

export const selectFreeRoundsCount = (state) => state.freeRounds.count;

export const selectFreeRoundsPerformed = (state) => state.freeRounds.performed;

export const selectFreeRoundsWin = (state) => state.freeRounds.win;

// DEPRECATED — alias for selectFreeRoundsWin (will be removed)
export const selectFreeRoundsTotalWin = (state) => state.freeRounds.win;

export const selectFreeRoundsDisplayedWin = (state) => state.freeRounds.displayedWin;

export const selectFreeRoundsWinCounted = (state) => state.freeRounds.winCounted;

export const selectFreeRoundsBet = (state) => state.freeRounds.betValue;

export const selectFreeRoundsStatus = (state) => state.freeRounds.status;

export const selectFreeRoundsCompletionPending = (state) =>
  state.freeRounds.completionPending;

export const selectFreeRoundsEnded = (state) =>
  state.freeRounds.freeRoundsEnded;

export const selectFreeRoundsReloadPending = (state) =>
  state.freeRounds.reloadPending;

export const selectFreeRoundsReloadWinWithheld = (state) =>
  state.freeRounds.reloadWinWithheld;


// DEPRECATED stubs — kept to avoid build breakage in legacy components
// (FreeRoundsWinDisplay, UserInterface primary/secondary)
// These will be removed when those files are rewritten.
export const selectIsFreeRoundsCompletionReload = () => false;
export const selectBalanceAlreadyUpdated = () => false;

// ==============================
// 🔹 Export Actions
// ==============================

export const {
  setFreeRounds,
  clearFreeRounds,
  setCompletionPending,
  clearCompletionPending,
  setFreeRoundsEnded,
  clearFreeRoundsEnded,
  setReloadPending,
  clearReloadPending,
  incrementDisplayedWin,
  clearFreeRoundsWin,
  applySyncedWin,
} = freeRoundsSlice.actions;

// DEPRECATED no-op action stubs — kept to avoid build breakage in:
// SceneTransitionOverlay, RoyalCollectSceneTransition
// These will be removed when those files are updated.
export const setBalanceAlreadyUpdated = () => ({ type: "freeRounds/noop" });
export const clearFreeRoundsCompletionReload = () => ({ type: "freeRounds/noop" });
export const incrementPerformed = () => ({ type: "freeRounds/noop" });
export const completeFreeRounds = () => ({ type: "freeRounds/noop" });

export default freeRoundsSlice.reducer;
